import React from 'react';
import {
  Box,
  BoxProps,
  Button,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { PRO_CTA_COLOR } from '../constants';

export const CheckoutFormContainer = styled(Box)<
  BoxProps & {
    component?: React.ElementType;
    onSubmit?: React.FormEventHandler<HTMLElement>;
  }
>(({ theme }) => ({
  display: 'flex',
  padding: '0 8px',
  marginTop: '32px',
  flexDirection: 'row',
  gap: '32px',
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
  },
}));

export const CheckoutContainerMobileView = styled(Box)<
  BoxProps & {
    component?: React.ElementType;
    onSubmit?: React.FormEventHandler<HTMLElement>;
  }
>({
  display: 'flex',
  flex: '1',
  flexDirection: 'column',
  position: 'relative',
});

export const CheckoutContainer = styled(Stack)(({ theme }) => ({
  padding: '24px',
  gap: '16px',
  borderRadius: '16px',
  backgroundColor: theme.palette.background.paper,
  width: '100%',
  boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.25)',
}));

export const CheckoutLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '14px',
  marginBottom: '5px',
}));

// Paper, not a gold wash: a full-panel gradient made the summary read as a
// promotional banner rather than an invoice. Gold survives only as a hairline
// at the top edge, so the panel is elegant and the accent still points at it.
export const CartCheckoutContainer = styled(Stack)(({ theme }) => ({
  position: 'relative',
  borderRadius: '18px',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  padding: '20px',
  gap: '12px',
  boxShadow: '0 10px 30px -22px rgba(16, 24, 40, 0.45)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.guidedCourse.gold}, ${theme.palette.guidedCourse.goldDeep})`,
  },
}));

export const CouponInputContainer = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  padding: '8px 10px 8px 12px',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  transition: 'border-color .15s ease',
  '&:focus-within': { borderColor: theme.palette.brand.gold },
}));

export const CouponInput = styled(TextField)(({ theme }) => ({
  border: 'none',
  padding: '0',
  fontSize: '14px',

  '& fieldset': { border: 'none' },
  '&:hover fieldset': { border: 'none' },
  '&.Mui-focused fieldset': { border: 'none' },
  '& .MuiInputBase-input': { padding: '0' },
  '& .MuiOutlinedInput-root': {
    color: theme.palette.text.primary,
    fontWeight: '400',
  },
}));

export const CouponDiscountTag = styled(Box)({
  padding: '4px 8px',
  background: 'rgba(56, 142, 60, 0.08)',
  borderRadius: '8px',
  marginTop: '8px',
  width: 'max-content',
});

// Same CTA as the product card: solid amber, white bold label, and the inset
// bottom lip that makes it read as a key rather than a tinted block.
export const CheckoutCta = styled(Button)(({ theme }) => ({
  width: '100%',
  padding: '15px 22px',
  borderRadius: '14px',
  fontSize: '15.5px',
  fontWeight: 800,
  letterSpacing: '0.01em',
  textTransform: 'none',
  color: '#fff',
  background: PRO_CTA_COLOR,
  boxShadow:
    'inset 0 -4px 0 rgba(0, 0, 0, 0.22), 0 12px 24px -12px rgba(245, 158, 11, 0.85)',
  transition: 'transform .14s ease, box-shadow .14s ease, filter .14s ease',
  '&:hover': {
    background: PRO_CTA_COLOR,
    filter: 'brightness(1.05)',
    transform: 'translateY(-2px)',
    boxShadow:
      'inset 0 -4px 0 rgba(0, 0, 0, 0.22), 0 18px 30px -12px rgba(245, 158, 11, 0.9)',
  },
  '&:active': {
    transform: 'translateY(2px)',
    boxShadow:
      'inset 0 -1px 0 rgba(0, 0, 0, 0.24), 0 6px 14px -10px rgba(245, 158, 11, 0.8)',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    boxShadow: 'none',
  },
}));
